import { useState } from 'react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import cx from 'classnames'

import Button from 'common/components/button'
import ConfirmationModal from 'common/components/confirmationModal/ConfirmationModal'
import { Bin, Edit, MoreOptionsVertical } from 'common/icons'

import useDeleteLanguageRuleMutation from '../../../api/mutations/useDeleteLanguageRuleMutation'

type Props = {
  ruleId: string
  onEdit: () => void
}

const LanguageRulesActions = ({ ruleId, onEdit }: Props) => {
  const [showDeleteRuleModal, setShowDeleteRuleModal] = useState<boolean>(false)
  const { mutate, isPending: isLoading } = useDeleteLanguageRuleMutation({
    ruleId,
    onSuccess: () => {
      setShowDeleteRuleModal(false)
    },
  })

  return (
    <>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <Button
            variant="icon"
            data-testid="Rule actions"
            suffixIcon={
              <MoreOptionsVertical
                isDefaultColor={false}
                className="text-base-700"
              />
            }
          />
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content className="p-2 z-40 bg-white border border-coolGray-200 rounded shadow-lg min-w-[120px]">
            <DropdownMenu.Item
              onSelect={onEdit}
              disabled={isLoading}
              aria-label="Edit rule"
              className={cx(
                'flex items-center hover:bg-gold-300 text-xs text-base-700 px-2 py-1 outline-none rounded',
                {
                  'cursor-not-allowed opacity-50': isLoading,
                  'cursor-pointer': !isLoading,
                }
              )}
            >
              <Edit
                isDefaultColor={false}
                size={4}
                className="mr-4 text-base-700"
              />
              Edit
            </DropdownMenu.Item>
            <DropdownMenu.Item
              onSelect={() => setShowDeleteRuleModal(true)}
              disabled={isLoading}
              aria-label="Delete rule"
              className={cx(
                'flex items-center hover:bg-gold-300 text-xs text-base-700 px-2 py-1 outline-none rounded',
                {
                  'cursor-not-allowed opacity-50': isLoading,
                  'cursor-pointer': !isLoading,
                }
              )}
            >
              <Bin
                isDefaultColor={false}
                size={4}
                className="mr-4 text-base-700"
              />
              Delete
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
      <ConfirmationModal
        data-testid="remove-rule-modal"
        title="Delete rule"
        confirmationText={`Are you sure you want to delete this rule? Existing content won't be updated, you will need to update existing content manually.`}
        confirmButtonText="YES, DELETE"
        cancelButtonText="CANCEL"
        open={showDeleteRuleModal}
        hasConfirmationSlider={true}
        onConfirm={() => mutate()}
        onCancel={() => setShowDeleteRuleModal(false)}
        isLoading={isLoading}
      />
    </>
  )
}

export default LanguageRulesActions
